import { useEffect, useState } from 'react';
import { IManagingLinuxServiceInstance } from '../../api/models';
import { EventType } from '../../constants/EventType';
import { LinuxServiceAction } from '../../constants/LinuxServiceAction';
import {
    ServerApplicationClientEventSummaryHandler,
    ServerApplicationClientEventSummaryHandlerArgs,
} from '../ApplicationServerManagement/ServerApplicationClientEventSummaryHandler';
import useRefedProp from '../../hooks/useRefedProp';
import { Permission } from '../../permissions/permission';
import { UserPrincipal } from '../../permissions/userPrincipal';
import { ServerApplicationClientEventSummary } from '../../signalr/models';
import signalREventsClient from '../../signalr/SignalREventsClient';
import { InfoModal } from '../Shared/InfoModal';
import { ActionButton } from '../UIExtentions/Buttons';
import { ServerLinuxServiceDetails } from '../LinuxServiceInfo/LinuxServiceInfoView';

export interface LinuxServicesServerRowActionsProps {
    serverApp: IManagingLinuxServiceInstance;
    userPrincipal: UserPrincipal;
    serverUserPrincipal: UserPrincipal;
    onClickCallback: (action: LinuxServiceAction) => Promise<void>;
    onTimeoutCallback: (data: ServerApplicationClientEventSummary) => void;
}

export function LinuxServicesServerRowActions(props: LinuxServicesServerRowActionsProps) {
    const [showInfo, setShowInfo] = useState(false);
    const [startSpinning, setStartSpinning] = useState(false);
    const [stopSpinning, setStopSpinning] = useState(false);
    const [restartSpinning, setRestartSpinning] = useState(false);
    const [refreshSpinning, setRefreshSpinning] = useState(false);
    const onTimeoutCallbackRef = useRefedProp(props.onTimeoutCallback);

    const serverApp = props.serverApp;
    const userPrincipal = props.userPrincipal;
    const serverUserPrincipal = props.serverUserPrincipal;
    const onClickCallback = props.onClickCallback;

    const getEnableState = function (permission: Permission): boolean {
        return userPrincipal.hasPermission(permission) || serverUserPrincipal?.hasPermission(permission);
    };

    useEffect(() => {
        const serviceStartHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.LinuxServiceStartTriggered],
            failedTypes: [EventType.LinuxServiceStartFailed],
            successTypes: [EventType.LinuxServiceStartSucceeded],
            emptyCallback: () => setStartSpinning(false),
            addCallback: () => setStartSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        const serviceStopHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.LinuxServiceStopTriggered],
            failedTypes: [EventType.LinuxServiceStopFailed],
            successTypes: [EventType.LinuxServiceStopSucceeded],
            emptyCallback: () => setStopSpinning(false),
            addCallback: () => setStopSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        const serviceRestartHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.LinuxServiceRestartTriggered],
            failedTypes: [EventType.LinuxServiceRestartFailed],
            successTypes: [EventType.LinuxServiceRestartSucceeded],
            emptyCallback: () => setRestartSpinning(false),
            addCallback: () => setRestartSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        const serviceRefreshHandler = new ServerApplicationClientEventSummaryHandler(serverApp.serverName, {
            addTypes: [EventType.LinuxServiceRefreshTriggered],
            failedTypes: [EventType.LinuxServiceRefreshFailed],
            successTypes: [EventType.LinuxServiceRefreshSucceeded],
            emptyCallback: () => setRefreshSpinning(false),
            addCallback: () => setRefreshSpinning(true),
            timeoutCallback: (data) => onTimeoutCallbackRef.current?.(data),
        } as ServerApplicationClientEventSummaryHandlerArgs);

        function isForMe(data: ServerApplicationClientEventSummary) {
            return data.targetApplication === serverApp.name && data.targetServer === serverApp.serverName;
        }

        signalREventsClient.onLinuxServiceEvent.setHook(`ServerRow_${serverApp.name}_${serverApp.serverName}`, (data) => {
            if (isForMe(data)) {
                serviceStartHandler.handleItem(data);
                serviceStopHandler.handleItem(data);
                serviceRestartHandler.handleItem(data);
                serviceRefreshHandler.handleItem(data);
            }
        });
    }, [onTimeoutCallbackRef, serverApp.name, serverApp.serverName]);

    const infoModalTitle = `${serverApp.name} - ${serverApp.serverName}`;

    return (
        <>
            <td>
                <ActionButton
                    disabled={!getEnableState(Permission.LinuxService_Refresh)}
                    spinning={refreshSpinning}
                    icon="fa-refresh"
                    variant="btn-dark-blue"
                    title="Refresh"
                    onClick={async () => await onClickCallback(LinuxServiceAction.Refresh)}
                />
            </td>
            <td>
                <ActionButton
                    disabled={!getEnableState(Permission.LinuxService_Start)}
                    spinning={startSpinning}
                    icon="fa-play"
                    variant="btn-green"
                    title="Start"
                    onClick={async () => await onClickCallback(LinuxServiceAction.Start)}
                />
                <ActionButton
                    disabled={!getEnableState(Permission.LinuxService_Stop)}
                    spinning={stopSpinning}
                    icon="fa-stop"
                    variant="btn-red"
                    title="Stop"
                    onClick={async () => await onClickCallback(LinuxServiceAction.Stop)}
                />
                <ActionButton
                    disabled={!getEnableState(Permission.LinuxService_Restart)}
                    spinning={restartSpinning}
                    icon="fa-step-forward"
                    variant="btn-blue"
                    title="Restart"
                    onClick={async () => await onClickCallback(LinuxServiceAction.Restart)}
                />
                |
                <ActionButton icon="fa-info-circle" variant="btn-dark-blue" title="Info" onClick={() => setShowInfo(true)} />
                <InfoModal title={infoModalTitle} show={showInfo} onHide={() => setShowInfo(false)}>
                    <ServerLinuxServiceDetails serverApp={serverApp}></ServerLinuxServiceDetails>
                </InfoModal>
            </td>
        </>
    );
}
